import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { PropertyCard } from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ArrowRight, Building2, ExternalLink, FileText, Heart, Loader2, Search, Share2, SlidersHorizontal, Trash2, MessageSquare, X, ClipboardCheck, CheckCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { scrollToElementByIdWithOffset } from '../lib/scroll';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SAVED_OPENRENT_STORAGE_KEY = 'saved_openrent_listings';
const OPENRENT_PAGE_SIZE = 24;

const normalizeSavedOpenRentListings = (entries) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  const seen = new Set();
  const normalized = [];

  for (const entry of entries) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }

    const externalUrl = typeof entry.external_url === 'string' ? entry.external_url.trim() : '';
    if (!externalUrl) {
      continue;
    }

    const dedupeKey = `${entry.id || ''}|${externalUrl}`;
    if (seen.has(dedupeKey)) {
      continue;
    }
    seen.add(dedupeKey);

    normalized.push({
      ...entry,
      external_url: externalUrl,
    });
  }

  return normalized;
};

const readSavedOpenRentListings = () => {
  try {
    const raw = localStorage.getItem(SAVED_OPENRENT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return normalizeSavedOpenRentListings(parsed);
  } catch (error) {
    console.error('Failed to load saved OpenRent listings:', error);
    return [];
  }
};

export const TenantExplore = () => {
  useEffect(() => { document.title = 'Explore Properties - SIYAK'; }, []);
  const { user, isTenant } = useAuth();
  const location = useLocation();
  const [savedProperties, setSavedProperties] = useState([]);
  const [applications, setApplications] = useState([]);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [openRentListings, setOpenRentListings] = useState([]);
  const [openRentMeta, setOpenRentMeta] = useState({ blocked_by_robots: false });
  const [openRentOffset, setOpenRentOffset] = useState(0);
  const [savedOpenRentListings, setSavedOpenRentListings] = useState(() => readSavedOpenRentListings());
  const [savedPropertyIds, setSavedPropertyIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalSearch, setInternalSearch] = useState('');
  const [internalMinRent, setInternalMinRent] = useState('');
  const [internalMaxRent, setInternalMaxRent] = useState('');
  const [internalBedrooms, setInternalBedrooms] = useState('');
  const [internalPropertyType, setInternalPropertyType] = useState('');
  const [showInternalFilters, setShowInternalFilters] = useState(false);
  const [showOpenRentFilters, setShowOpenRentFilters] = useState(false);
  const [showUnifiedFilters, setShowUnifiedFilters] = useState(false);
  const [draftExploreFilters, setDraftExploreFilters] = useState(() => ({
    location: '',
    minPrice: '',
    maxPrice: '',
    bedrooms: '',
    propertyType: '',
  }));
  const [openRentLoading, setOpenRentLoading] = useState(false);
  const [openRentLoadingMore, setOpenRentLoadingMore] = useState(false);
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
  const [withdrawAppId, setWithdrawAppId] = useState(null);
  const [applicationMessageDrafts, setApplicationMessageDrafts] = useState({});
  const [applicationMessageSending, setApplicationMessageSending] = useState({});
  const [screeningAnswers, setScreeningAnswers] = useState({});
  const [screeningFollowUps, setScreeningFollowUps] = useState({});
  const [screeningSubmitting, setScreeningSubmitting] = useState({});
  const [exploreFilters, setExploreFilters] = useState({
    location: '',
    minPrice: '',
    maxPrice: '',
    bedrooms: '',
    propertyType: '',
  });
  const openRentRequestSeqRef = useRef(0);

  // Cheap string similarity helpers for postcode/address proximity fallback
  const normalizeStr = (s) => (s || '').toString().toUpperCase().replace(/\s+/g, '').replace(/[^A-Z0-9]/g, '');

  const levenshtein = (a, b) => {
    if (!a) return b.length;
    if (!b) return a.length;
    const matrix = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
    for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
    for (let j = 0; j <= b.length; j++) matrix[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );
      }
    }
    return matrix[a.length][b.length];
  };

  const proximityScore = (search, target) => {
    const s = normalizeStr(search);
    const t = normalizeStr(target);
    if (!s) return Number.MAX_SAFE_INTEGER;
    if (!t) return Number.MAX_SAFE_INTEGER - 1;
    if (s === t) return 0;
    if (t.startsWith(s) || s.startsWith(t)) return 1;
    const d = levenshtein(s, t);
    // normalize by length to favor shorter absolute distances
    return Math.round((d / Math.max(s.length, t.length)) * 100);
  };
  useEffect(() => {
    try {
      const normalized = normalizeSavedOpenRentListings(savedOpenRentListings);
      localStorage.setItem(SAVED_OPENRENT_STORAGE_KEY, JSON.stringify(normalized));
    } catch (error) {
      console.error('Failed to persist saved OpenRent listings:', error);
    }
  }, [savedOpenRentListings]);

  const hasOpenRentFilterInput = useCallback((active) => {
    return Boolean(
      active.location.trim() ||
      active.minPrice !== '' ||
      active.maxPrice !== '' ||
      active.bedrooms !== '' ||
      active.propertyType !== ''
    );
  }, []);

  useEffect(() => {
    if (loading) {
      return undefined;
    }

    const sectionId = (() => {
      if (location.hash === '#applications' || location.hash === '#applications-section') {
        return 'applications-section';
      }

      if (location.hash === '#browse' || location.hash === '#browse-section') {
        return 'browse-section';
      }

      return null;
    })();

    if (!sectionId) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      scrollToElementByIdWithOffset(sectionId);
    }, 100);

    return () => window.clearTimeout(timerId);
  }, [loading, location.hash]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const applicationId = params.get('applicationId');
    if (!applicationId || loading) return undefined;
    const timerId = window.setTimeout(() => {
      const element = document.getElementById(`application-${applicationId}`);
      if (element) {
        scrollToElementByIdWithOffset(`application-${applicationId}`);
      } else {
        scrollToElementByIdWithOffset('applications-section');
      }
    }, 100);
    return () => window.clearTimeout(timerId);
  }, [applications, loading, location.search]);

  // Poll for updated application status every 10 seconds
  useEffect(() => {
    const pollInterval = setInterval(async () => {
      try {
        const appsRes = await axios.get(`${API_URL}/api/applications`, { withCredentials: true });
        setApplications(appsRes.data);
      } catch (error) {
        console.error('Failed to poll applications:', error);
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(pollInterval);
  }, []);

  const fetchOpenRentListings = useCallback(async (overrideFilters = null, options = {}) => {
    const { append = false, offset = 0, allowTopup = true, forceRefresh = false } = options;
    const activeFilters = overrideFilters || exploreFilters;
    if (!hasOpenRentFilterInput(activeFilters)) {
      openRentRequestSeqRef.current += 1;
      setOpenRentListings([]);
      setOpenRentMeta({ blocked_by_robots: false });
      setOpenRentOffset(0);
      setOpenRentLoading(false);
      setOpenRentLoadingMore(false);
      return;
    }

    const requestSeq = openRentRequestSeqRef.current + 1;
    openRentRequestSeqRef.current = requestSeq;

    const params = {
      source: 'openrent',
      limit: OPENRENT_PAGE_SIZE,
      offset,
      allow_topup: allowTopup,
    };

    if (forceRefresh) {
      params.force_refresh = true;
    }

    if (activeFilters.location.trim()) {
      params.location = activeFilters.location.trim();
    }
    if (activeFilters.minPrice !== '') {
      params.min_price = Number(activeFilters.minPrice);
    }
    if (activeFilters.maxPrice !== '') {
      params.max_price = Number(activeFilters.maxPrice);
    }
    if (activeFilters.bedrooms !== '') {
      params.bedrooms = Number(activeFilters.bedrooms);
    }
    if (activeFilters.propertyType !== '') {
      params.property_type = activeFilters.propertyType;
    }

    if (append) {
      setOpenRentLoadingMore(true);
    } else {
      setOpenRentLoading(true);
    }
    try {
      const response = await axios.get(`${API_URL}/api/explore/listings`, {
        params,
        withCredentials: true,
      });
      if (requestSeq !== openRentRequestSeqRef.current) {
        return;
      }
      const incoming = response.data.listings || [];
      if (append) {
        setOpenRentListings((prev) => {
          const seen = new Set(prev.map((item) => item.external_url || item.id));
          const next = [...prev];
          for (const item of incoming) {
            const key = item.external_url || item.id;
            if (!seen.has(key)) {
              seen.add(key);
              next.push(item);
            }
          }
          return next;
        });
      } else {
        setOpenRentListings(incoming);
      }

      setOpenRentMeta(response.data.meta || { blocked_by_robots: false });
      setOpenRentOffset(offset + incoming.length);
    } catch (error) {
      if (requestSeq !== openRentRequestSeqRef.current) {
        return;
      }
      console.error('Failed to fetch OpenRent listings:', error);
      toast.error(error.response?.data?.detail || 'Failed to load OpenRent listings');
      if (!append) {
        setOpenRentListings([]);
        setOpenRentMeta({ blocked_by_robots: false });
        setOpenRentOffset(0);
      }
    } finally {
      if (requestSeq === openRentRequestSeqRef.current) {
        if (append) {
          setOpenRentLoadingMore(false);
        } else {
          setOpenRentLoading(false);
        }
      }
    }
  }, [exploreFilters, hasOpenRentFilterInput]);

  const fetchExploreData = useCallback(async () => {
    try {
      const [savedRes, appsRes, propsRes] = await Promise.all([
        axios.get(`${API_URL}/api/saved-properties`, { withCredentials: true }),
        axios.get(`${API_URL}/api/applications`, { withCredentials: true }),
        axios.get(`${API_URL}/api/properties/browse/all`, { withCredentials: true })
      ]);
      setSavedProperties(savedRes.data);
      setApplications(appsRes.data);
      setAvailableProperties(propsRes.data);
      const savedIds = new Set(savedRes.data.map(s => s.property_id));
      setSavedPropertyIds(savedIds);
    } catch (error) {
      console.error('Failed to fetch explore data:', error);
      toast.error('Failed to load explore data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExploreData();
  }, [fetchExploreData]);

  const fetchInternalProperties = useCallback(async (filters = null) => {
    const active = filters || {
      search: internalSearch,
      minRent: internalMinRent,
      maxRent: internalMaxRent,
      bedrooms: internalBedrooms,
      propertyType: internalPropertyType,
    };
    setInternalLoading(true);
    try {
      const params = new URLSearchParams();
      if (active.search) params.append('search', active.search);
      if (active.minRent) params.append('min_rent', active.minRent);
      if (active.maxRent) params.append('max_rent', active.maxRent);
      if (active.bedrooms && active.bedrooms !== 'any') params.append('bedrooms', active.bedrooms);
      if (active.propertyType && active.propertyType !== 'any') params.append('property_type', active.propertyType);
      const response = await axios.get(`${API_URL}/api/properties/browse/all?${params.toString()}`, { withCredentials: true });
      setAvailableProperties(response.data);
    } catch (error) {
      console.error('Failed to fetch internal properties:', error);
      toast.error('Failed to load internal listings');
    } finally {
      setInternalLoading(false);
    }
  }, [internalSearch, internalMinRent, internalMaxRent, internalBedrooms, internalPropertyType]);

  const handleInternalSearch = (e) => {
    e.preventDefault();
    fetchInternalProperties();
  };

  const clearInternalFilters = () => {
    const cleared = { search: '', minRent: '', maxRent: '', bedrooms: '', propertyType: '' };
    setInternalSearch('');
    setInternalMinRent('');
    setInternalMaxRent('');
    setInternalBedrooms('');
    setInternalPropertyType('');
    fetchInternalProperties(cleared);
  };

  const hasInternalActiveFilters = internalSearch || internalMinRent || internalMaxRent || internalBedrooms || internalPropertyType;

  const mapExploreToInternalFilters = useCallback((activeExplore) => ({
    search: activeExplore.location || '',
    minRent: activeExplore.minPrice || '',
    maxRent: activeExplore.maxPrice || '',
    bedrooms: activeExplore.bedrooms || '',
    propertyType: activeExplore.propertyType || '',
  }), []);

  const applyExploreFilters = useCallback((activeExplore, options = {}) => {
    const { forceRefreshExternal = true } = options;
    const normalized = {
      location: activeExplore.location || '',
      minPrice: activeExplore.minPrice || '',
      maxPrice: activeExplore.maxPrice || '',
      bedrooms: activeExplore.bedrooms || '',
      propertyType: activeExplore.propertyType || '',
    };

    setExploreFilters(normalized);
    setDraftExploreFilters(normalized);

    const internalFilters = mapExploreToInternalFilters(normalized);
    fetchInternalProperties(internalFilters);

    if (hasOpenRentFilterInput(normalized)) {
      setOpenRentLoading(true);
      fetchOpenRentListings(normalized, { append: false, offset: 0, allowTopup: true, forceRefresh: forceRefreshExternal });
    } else {
      openRentRequestSeqRef.current += 1;
      setOpenRentListings([]);
      setOpenRentMeta({ blocked_by_robots: false });
      setOpenRentOffset(0);
      setOpenRentLoading(false);
      setOpenRentLoadingMore(false);
    }
  }, [fetchInternalProperties, fetchOpenRentListings, hasOpenRentFilterInput, mapExploreToInternalFilters]);

  const applyOpenRentFilters = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!hasOpenRentFilterInput(exploreFilters)) {
      setOpenRentListings([]);
      setOpenRentMeta({ blocked_by_robots: false });
      setOpenRentOffset(0);
      return;
    }
    fetchOpenRentListings(exploreFilters, { append: false, offset: 0, allowTopup: true, forceRefresh: true });
  };

  const clearOpenRentFilters = () => {
    openRentRequestSeqRef.current += 1;
    setExploreFilters({ location: '', minPrice: '', maxPrice: '', bedrooms: '', propertyType: '' });
    setOpenRentListings([]);
    setOpenRentMeta({ blocked_by_robots: false });
    setOpenRentOffset(0);
  };

  const loadMoreOpenRentListings = () => {
    if (openRentLoading || openRentLoadingMore) {
      return;
    }
    fetchOpenRentListings(exploreFilters, { append: true, offset: openRentOffset, allowTopup: true });
  };

  const isOpenRentSaved = useCallback((listing) => {
    if (!listing) {
      return false;
    }
    return savedOpenRentListings.some((entry) => entry.id === listing.id || entry.external_url === listing.external_url);
  }, [savedOpenRentListings]);

  const handleToggleSaveOpenRentListing = useCallback((listing) => {
    if (!listing) {
      return;
    }

    setSavedOpenRentListings((prev) => {
      const exists = prev.some((entry) => entry.id === listing.id || entry.external_url === listing.external_url);
      if (exists) {
        toast.success('Removed from saved OpenRent listings');
        return prev.filter((entry) => entry.id !== listing.id && entry.external_url !== listing.external_url);
      }

      toast.success('Saved OpenRent listing');
      return [
        {
          ...listing,
          saved_at: new Date().toISOString(),
        },
        ...prev,
      ];
    });
  }, []);

  const handleShareOpenRentListing = useCallback(async (listing) => {
    if (!listing?.external_url) {
      toast.error('No listing URL available to share');
      return;
    }

    const sharePayload = {
      title: listing.title || 'OpenRent listing',
      text: listing.address || 'Check this OpenRent listing',
      url: listing.external_url,
    };

    try {
      if (navigator.share) {
        await navigator.share(sharePayload);
        return;
      }

      await navigator.clipboard.writeText(listing.external_url);
      toast.success('Listing link copied to clipboard');
    } catch (error) {
      console.error('Share failed:', error);
      toast.error('Could not share listing');
    }
  }, []);

  const internalSavedProperties = savedProperties.filter((saved) => Boolean(saved.property));
  const hasAnySavedProperties = internalSavedProperties.length > 0 || savedOpenRentListings.length > 0;

  const handleSaveProperty = async (propertyId) => {
    if (!isTenant()) {
      toast.error('Only tenants can save properties');
      return;
    }

    try {
      if (savedPropertyIds.has(propertyId)) {
        await axios.delete(`${API_URL}/api/saved-properties/${propertyId}`, { withCredentials: true });
        setSavedPropertyIds(prev => {
          const next = new Set(prev);
          next.delete(propertyId);
          return next;
        });
        setSavedProperties((prev) => prev.filter((saved) => saved.property_id !== propertyId));
        toast.success('Property removed from saved');
      } else {
        await axios.post(`${API_URL}/api/saved-properties`, { property_id: propertyId }, { withCredentials: true });
        setSavedPropertyIds(prev => new Set([...prev, propertyId]));
        // Find the property and add it to saved properties
        const property = availableProperties.find(p => p.id === propertyId);
        if (property) {
          setSavedProperties(prev => [...prev, { 
            id: propertyId, 
            property_id: propertyId, 
            property 
          }]);
        }
        toast.success('Property saved successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    }
  };

  const handleUnsave = async (propertyId) => {
    try {
      await axios.delete(`${API_URL}/api/saved-properties/${propertyId}`, { withCredentials: true });
      setSavedProperties((prev) => prev.filter((saved) => saved.property_id !== propertyId));
      setSavedPropertyIds(prev => {
        const next = new Set(prev);
        next.delete(propertyId);
        return next;
      });
      toast.success('Property removed from saved');
    } catch (error) {
      toast.error('Failed to remove property');
    }
  };

  const handleWithdrawApplicationClick = (applicationId) => {
    setWithdrawAppId(applicationId);
    setWithdrawConfirmOpen(true);
  };

  const handleWithdrawApplication = async () => {
    try {
      await axios.delete(`${API_URL}/api/applications/${withdrawAppId}`, { withCredentials: true });
      setApplications((prev) => prev.filter((app) => app.id !== withdrawAppId));
      toast.success('Application removed');
      setWithdrawConfirmOpen(false);
      setWithdrawAppId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw application');
    }
  };

  const handleSendApplicationMessage = async (application) => {
    const draft = (applicationMessageDrafts[application.id] || '').trim();
    if (!draft) {
      toast.error('Message cannot be empty');
      return;
    }

    setApplicationMessageSending((prev) => ({ ...prev, [application.id]: true }));
    try {
      const response = await axios.post(
        `${API_URL}/api/applications/${application.id}/messages`,
        { body: draft },
        { withCredentials: true }
      );
      const messageDoc = response.data;
      const sentAt = messageDoc.created_at || new Date().toISOString();
      setApplications((prev) => prev.map((app) => {
        if (app.id !== application.id) {
          return app;
        }
        return {
          ...app,
          application_messages: [...(app.application_messages || []), messageDoc],
          latest_application_message: messageDoc.body,
          latest_application_message_at: sentAt,
          application_unread_count: 0,
        };
      }));
      setApplicationMessageDrafts((prev) => ({ ...prev, [application.id]: '' }));
      toast.success('Message sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setApplicationMessageSending((prev) => ({ ...prev, [application.id]: false }));
    }
  };

  const handleSubmitScreening = async (app) => {
    const questions = app.screening_questions || [];
    const answers = screeningAnswers[app.id] || {};
    const followUps = screeningFollowUps[app.id] || {};

    const answerList = questions.map(q => ({
      question_id: q.question_id,
      answer: answers[q.question_id] || '',
      follow_up_answer: followUps[q.question_id] || null,
    }));

    setScreeningSubmitting(prev => ({ ...prev, [app.id]: true }));
    try {
      const res = await axios.post(
        `${API_URL}/api/applications/${app.id}/submit-screening`,
        { answers: answerList },
        { withCredentials: true }
      );
      setApplications(prev => prev.map(a => a.id === app.id ? { ...a, ...res.data } : a));
      setScreeningAnswers(prev => { const next = { ...prev }; delete next[app.id]; return next; });
      setScreeningFollowUps(prev => { const next = { ...prev }; delete next[app.id]; return next; });
      toast.success('Screening questionnaire submitted.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit screening answers.');
    } finally {
      setScreeningSubmitting(prev => ({ ...prev, [app.id]: false }));
    }
  };

  const getApplicationDecisionStatus = (application) => application?.decision_status || application?.status || 'pending';

  const getApplicationStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case 'responded':
        return <Badge className="bg-purple-100 text-purple-700">Responded</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-royal-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-navy-50 min-h-screen" data-testid="tenant-explore-page">
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
            <h1 className="font-heading text-3xl font-bold text-navy-900">Explore</h1>
            <p className="text-muted-foreground">
              Save properties, manage applications, and browse new listings.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8">
          <section className="space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-navy-900">Saved properties</h2>
            {hasAnySavedProperties ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {internalSavedProperties.map((saved) => (
                  saved.property && (
                    <PropertyCard
                      key={saved.id}
                      property={saved.property}
                      onSave={() => handleUnsave(saved.property_id)}
                      isSaved={true}
                    />
                  )
                ))}
                {savedOpenRentListings.map((listing) => {
                  const saved = isOpenRentSaved(listing);
                  return (
                    <div key={`saved-openrent-${listing.id || listing.external_url}`} className="space-y-2">
                      <PropertyCard
                        property={listing}
                        showActions={false}
                        useOverlayActions={true}
                        isSaved={saved}
                        onSave={() => handleToggleSaveOpenRentListing(listing)}
                        onShare={() => handleShareOpenRentListing(listing)}
                      />
                      <Button asChild className="w-full">
                        <a href={listing.external_url} target="_blank" rel="noreferrer noopener">
                          View on OpenRent
                          <ExternalLink className="h-4 w-4 ml-2" />
                        </a>
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Card className="p-10 text-center">
                <Heart className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-heading font-semibold text-lg mb-2">No saved properties yet</h3>
                <p className="text-muted-foreground mb-4">
                  Save listings you want to revisit.
                </p>
                <Button asChild>
                  <Link to="/tenant/explore#browse">
                    <Search className="h-4 w-4 mr-2" />
                    Browse listings
                  </Link>
                </Button>
              </Card>
            )}
          </section>

          <section className="space-y-4" id="applications-section">
            <h2 className="font-heading text-2xl font-semibold text-navy-900">Applications</h2>
            {applications.length > 0 ? (
              <div className="space-y-4">
                {applications.map((app) => {
                  const appAnswers = screeningAnswers[app.id] || {};
                  const appFollowUps = screeningFollowUps[app.id] || {};
                  const questions = app.screening_questions || [];
                  const isScreeningComplete = questions.length > 0 && questions.every(q => {
                    const a = appAnswers[q.question_id];
                    return a !== undefined && a !== '';
                  });

                  return (
                    <Card
                      key={app.id}
                      id={`application-${app.id}`}
                      className={new URLSearchParams(location.search || '').get('applicationId') === app.id ? 'border-royal-500 bg-royal-50' : ''}
                    >
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="mb-1 flex items-center gap-2 flex-wrap">
                              {getApplicationStatusBadge(getApplicationDecisionStatus(app))}
                              {app.screening_status === 'sent' && (
                                <Badge className="bg-amber-100 text-amber-700">Screening Sent</Badge>
                              )}
                              {app.screening_status === 'completed' && (
                                <Badge className="bg-green-100 text-green-700">Screening Complete</Badge>
                              )}
                              {app.landlord_reply && <span className="flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200"><MessageSquare className="h-3 w-3" /> New Reply</span>}
                            </div>
                            <h3 className="font-heading font-semibold text-lg">
                              {app.property_title || 'Property'}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              Applied on {new Date(app.created_at).toLocaleDateString('en-GB')}
                            </p>

                            {/* Your Application Message */}
                            {app.message && (
                              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                                <p className="text-xs font-semibold text-green-900 mb-1">Your Message</p>
                                <p className="text-sm text-green-900">{app.message}</p>
                              </div>
                            )}

                            {Array.isArray(app.application_messages) && app.application_messages.length > 0 && (
                              <div className="mt-4 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-4">
                                <p className="text-xs font-semibold text-slate-700">Application conversation</p>
                                {app.application_messages.map((message) => (
                                  <div
                                    key={message.id}
                                    className={`rounded-md p-3 text-sm whitespace-pre-wrap ${message.sender_id === user?.id ? 'bg-green-100 text-green-950' : 'bg-blue-50 text-blue-900 border border-blue-100'}`}
                                  >
                                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                      {message.sender_name || (message.sender_id === user?.id ? 'You' : 'Landlord')}
                                    </p>
                                    <p>{message.body}</p>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Landlord Reply Section */}
                            {app.landlord_reply && (!Array.isArray(app.application_messages) || app.application_messages.length === 0) && (
                              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                                <p className="text-xs font-semibold text-blue-900 mb-1">Landlord Reply</p>
                                <p className="text-sm text-blue-900">{app.landlord_reply}</p>
                                <p className="text-xs text-blue-700 mt-1">
                                  {new Date(app.landlord_reply_at).toLocaleString('en-GB')}
                                </p>
                              </div>
                            )}

                            {getApplicationDecisionStatus(app) !== 'rejected' && (
                              <div className="mt-4 space-y-2 rounded-md border border-slate-200 p-4">
                                <Label htmlFor={`app-message-${app.id}`} className="text-xs font-semibold text-slate-700">Send a follow-up</Label>
                                <Textarea
                                  id={`app-message-${app.id}`}
                                  value={applicationMessageDrafts[app.id] || ''}
                                  onChange={(event) => setApplicationMessageDrafts((prev) => ({
                                    ...prev,
                                    [app.id]: event.target.value,
                                  }))}
                                  placeholder="Ask a question or send a follow-up message"
                                  className="min-h-[96px]"
                                />
                                <div className="flex justify-end">
                                  <Button
                                    type="button"
                                    onClick={() => handleSendApplicationMessage(app)}
                                    disabled={applicationMessageSending[app.id] || !(applicationMessageDrafts[app.id] || '').trim()}
                                  >
                                    {applicationMessageSending[app.id] ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageSquare className="h-4 w-4 mr-2" />}
                                    Send message
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* Pre-Screening Form (sent, not yet completed) */}
                            {app.screening_status === 'sent' && questions.length > 0 && (
                              <div className="mt-4 border border-purple-200 rounded-lg p-4 bg-purple-50">
                                <p className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
                                  <ClipboardCheck className="h-4 w-4" />
                                  Complete Pre-Screening Questionnaire
                                </p>
                                <div className="space-y-4">
                                  {questions.map((q) => {
                                    const currentAnswer = appAnswers[q.question_id];
                                    const currentFollowUp = appFollowUps[q.question_id] || '';
                                    const showFollowUp = q.follow_up && (currentAnswer === 'no' || currentAnswer === 'No');
                                    return (
                                      <div key={q.question_id} className="bg-white rounded-lg p-3 border border-purple-100">
                                        <p className="text-sm font-medium mb-2">{q.question_text}</p>
                                        {q.answer_type === 'yes_no' && (
                                          <div className="flex gap-2">
                                            <button
                                              type="button"
                                              onClick={() => setScreeningAnswers(prev => ({
                                                ...prev,
                                                [app.id]: { ...prev[app.id], [q.question_id]: 'yes' }
                                              }))}
                                              className={`flex-1 p-2 rounded-lg border text-sm transition-colors ${currentAnswer === 'yes' ? 'bg-green-100 border-green-500 text-green-900 ring-2 ring-green-200' : 'bg-green-50 border-green-200 hover:bg-green-100 cursor-pointer'}`}
                                            >
                                              Yes
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => setScreeningAnswers(prev => ({
                                                ...prev,
                                                [app.id]: { ...prev[app.id], [q.question_id]: 'no' }
                                              }))}
                                              className={`flex-1 p-2 rounded-lg border text-sm transition-colors ${currentAnswer === 'no' ? 'bg-red-100 border-red-500 text-red-900 ring-2 ring-red-200' : 'bg-red-50 border-red-200 hover:bg-red-100 cursor-pointer'}`}
                                            >
                                              No
                                            </button>
                                          </div>
                                        )}
                                        {q.answer_type === 'text' && (
                                          <Textarea
                                            placeholder="Your answer..."
                                            value={currentAnswer || ''}
                                            onChange={(e) => setScreeningAnswers(prev => ({
                                              ...prev,
                                              [app.id]: { ...prev[app.id], [q.question_id]: e.target.value }
                                            }))}
                                            className="min-h-[80px]"
                                          />
                                        )}
                                        {q.answer_type === 'date' && (
                                          <input
                                            type="date"
                                            value={currentAnswer || ''}
                                            onChange={(e) => setScreeningAnswers(prev => ({
                                              ...prev,
                                              [app.id]: { ...prev[app.id], [q.question_id]: e.target.value }
                                            }))}
                                            className="w-full border rounded-md px-3 py-2 text-sm"
                                          />
                                        )}
                                        {showFollowUp && (
                                          <div className="mt-2 pl-3 border-l-2 border-purple-200">
                                            <Label className="text-xs text-muted-foreground">{q.follow_up}</Label>
                                            <Textarea
                                              placeholder="Please elaborate..."
                                              value={currentFollowUp}
                                              onChange={(e) => setScreeningFollowUps(prev => ({
                                                ...prev,
                                                [app.id]: { ...prev[app.id], [q.question_id]: e.target.value }
                                              }))}
                                              className="mt-1 min-h-[60px]"
                                            />
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="mt-4 flex justify-end">
                                  <Button
                                    className="bg-purple-600 hover:bg-purple-700 text-white"
                                    disabled={!isScreeningComplete || screeningSubmitting[app.id]}
                                    onClick={() => handleSubmitScreening(app)}
                                  >
                                    {screeningSubmitting[app.id] ? (
                                      <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Submitting...
                                      </>
                                    ) : (
                                      <>
                                        <ClipboardCheck className="h-4 w-4 mr-2" />
                                        Submit Answers
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* Completed screening — read-only summary */}
                            {app.screening_status === 'completed' && questions.length > 0 && (
                              <div className="mt-4 border border-green-200 rounded-lg p-4 bg-green-50">
                                <p className="text-sm font-semibold text-green-900 mb-3 flex items-center gap-2">
                                  <CheckCircle className="h-4 w-4" />
                                  Your Screening Answers
                                </p>
                                <div className="space-y-3">
                                  {questions.map((q) => {
                                    const answerObj = (app.screening_answers || []).find(a => a.question_id === q.question_id);
                                    const answer = answerObj?.answer;
                                    const followUpAnswer = answerObj?.follow_up_answer;

                                    let answerDisplay;
                                    if (q.answer_type === 'yes_no') {
                                      const isYes = answer === 'yes' || answer === 'Yes';
                                      const isNo = answer === 'no' || answer === 'No';
                                      answerDisplay = (
                                        <Badge className={isYes ? 'bg-green-100 text-green-700' : isNo ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}>
                                          {isYes ? 'Yes' : isNo ? 'No' : answer || '—'}
                                        </Badge>
                                      );
                                    } else if (q.answer_type === 'date') {
                                      let formatted = answer || '—';
                                      if (answer) {
                                        try { formatted = new Date(answer).toLocaleDateString('en-GB'); } catch { /* keep raw */ }
                                      }
                                      answerDisplay = <span className="text-sm">{formatted}</span>;
                                    } else {
                                      answerDisplay = <span className="text-sm whitespace-pre-wrap">{answer || '—'}</span>;
                                    }

                                    return (
                                      <div key={q.question_id} className="bg-white rounded-lg p-3 border border-green-100">
                                        <p className="text-xs font-medium text-gray-700 mb-1">{q.question_text}</p>
                                        {answerDisplay}
                                        {followUpAnswer && (
                                          <div className="mt-2 pl-3 border-l-2 border-gray-200">
                                            <p className="text-xs text-muted-foreground mb-0.5">{q.follow_up}</p>
                                            <p className="text-sm">{followUpAnswer}</p>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button variant="outline" asChild>
                              <Link to={`/properties/${app.property_id}`}>
                                View property
                                <ArrowRight className="h-4 w-4 ml-2" />
                              </Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleWithdrawApplicationClick(app.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="p-10 text-center">
                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-heading font-semibold text-lg mb-2">No applications yet</h3>
                <p className="text-muted-foreground mb-4">
                  Apply for listings that fit your needs.
                </p>
                <Button asChild>
                  <Link to="/tenant/explore#browse">
                    <Search className="h-4 w-4 mr-2" />
                    Browse listings
                  </Link>
                </Button>
              </Card>
            )}
          </section>

          <section id="browse-section">
            <h2 className="font-heading text-2xl font-semibold text-navy-900 mb-2">Browse listings</h2>
            <p className="text-muted-foreground mb-2">Search across available properties and third-party listings.</p>
            <p className="text-xs text-muted-foreground mb-6">External listings may not reflect current availability.</p>

            <Card className="p-4 mb-6">
              <form onSubmit={(e) => {
                  e.preventDefault();
                  const activeExplore = { ...draftExploreFilters };
                  setAvailableProperties([]);
                  setOpenRentListings([]);
                  setOpenRentMeta({ blocked_by_robots: false });
                  setOpenRentOffset(0);
                  setInternalLoading(true);
                  applyExploreFilters(activeExplore, { forceRefreshExternal: true });
                  }} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      name="location"
                      type="text"
                      placeholder="Search by location, postcode, or keyword..."
                      value={draftExploreFilters.location}
                      onChange={(e) => setDraftExploreFilters((prev) => ({ ...prev, location: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                  <Button type="submit">
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowUnifiedFilters(!showUnifiedFilters)}
                  >
                    <SlidersHorizontal className="h-4 w-4 mr-2" />
                    Filters
                  </Button>
                </div>

                {showUnifiedFilters && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Min Price (£)</label>
                      <Input
                        name="minPrice"
                        type="number"
                        min="0"
                        placeholder="Min"
                        value={draftExploreFilters.minPrice}
                        onChange={(e) => setDraftExploreFilters((prev) => ({ ...prev, minPrice: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Max Price (£)</label>
                      <Input
                        name="maxPrice"
                        type="number"
                        min="0"
                        placeholder="Max"
                        value={draftExploreFilters.maxPrice}
                        onChange={(e) => setDraftExploreFilters((prev) => ({ ...prev, maxPrice: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Bedrooms (min)</label>
                      <Input
                        name="bedrooms"
                        type="number"
                        min="0"
                        placeholder="Any"
                        value={draftExploreFilters.bedrooms}
                        onChange={(e) => setDraftExploreFilters((prev) => ({ ...prev, bedrooms: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Property Type</label>
                      <Select
                        value={draftExploreFilters.propertyType || 'any'}
                        onValueChange={(val) => setDraftExploreFilters((prev) => ({ ...prev, propertyType: val === 'any' ? '' : val }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="house">Houses</SelectItem>
                          <SelectItem value="flat">Flats</SelectItem>
                          <SelectItem value="room">Rooms</SelectItem>
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="propertyType" value={draftExploreFilters.propertyType || ''} />
                    </div>
                  </div>
                )}

                {(exploreFilters.minPrice !== '' || exploreFilters.maxPrice !== '' || exploreFilters.bedrooms !== '' || exploreFilters.propertyType !== '') && (
                  <div className="flex flex-wrap items-center gap-2 pt-2">
                    <span className="text-sm text-muted-foreground">Active filters:</span>
                    {exploreFilters.minPrice !== '' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium">
                        Min £{exploreFilters.minPrice}
                        <button type="button" onClick={() => applyExploreFilters({ ...exploreFilters, minPrice: '' }, { forceRefreshExternal: true })} className="ml-0.5 hover:text-destructive"><X className="h-3 w-3" /></button>
                      </span>
                    )}
                    {exploreFilters.maxPrice !== '' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium">
                        Max £{exploreFilters.maxPrice}
                        <button type="button" onClick={() => applyExploreFilters({ ...exploreFilters, maxPrice: '' }, { forceRefreshExternal: true })} className="ml-0.5 hover:text-destructive"><X className="h-3 w-3" /></button>
                      </span>
                    )}
                    {exploreFilters.bedrooms !== '' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium">
                        {exploreFilters.bedrooms}+ bed
                        <button type="button" onClick={() => applyExploreFilters({ ...exploreFilters, bedrooms: '' }, { forceRefreshExternal: true })} className="ml-0.5 hover:text-destructive"><X className="h-3 w-3" /></button>
                      </span>
                    )}
                    {exploreFilters.propertyType !== '' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium capitalize">
                        {exploreFilters.propertyType}
                        <button type="button" onClick={() => applyExploreFilters({ ...exploreFilters, propertyType: '' }, { forceRefreshExternal: true })} className="ml-0.5 hover:text-destructive"><X className="h-3 w-3" /></button>
                      </span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => applyExploreFilters({ ...exploreFilters, minPrice: '', maxPrice: '', bedrooms: '', propertyType: '' }, { forceRefreshExternal: true })}
                      className="text-destructive"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Clear all
                    </Button>
                  </div>
                )}
              </form>
            </Card>

            {!internalLoading && (availableProperties.length > 0 || openRentListings.length > 0) && (
              <div className="mb-4 text-sm text-muted-foreground">
                {`${availableProperties.length} internal, ${openRentListings.length} external results`}
              </div>
            )}

            {(internalLoading || (openRentLoading && availableProperties.length === 0)) ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-royal-600" />
              </div>
            ) : (availableProperties.length > 0 || openRentListings.length > 0) ? (
              (() => {
                const searchKey = (exploreFilters.location || '').trim();
                if (!searchKey) {
                  // preserve internal-first ordering when no search key present
                  const merged = [
                    ...availableProperties.map((p) => ({ source: 'internal', item: p })),
                    ...openRentListings.map((l) => ({ source: 'openrent', item: l })),
                  ];
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {merged.map((entry) => {
                        if (entry.source === 'internal') {
                          const property = entry.item;
                          return (
                            <PropertyCard
                              key={`internal-${property.id}`}
                              property={property}
                              onSave={isTenant() ? handleSaveProperty : null}
                              isSaved={savedPropertyIds.has(property.id)}
                            />
                          );
                        }
                        const listing = entry.item;
                        const saved = isOpenRentSaved(listing);
                        const listingStatus = listing.status || 'available';
                        const isLetAgreed = listingStatus === 'let_agreed';
                        const isUnavailable = listingStatus === 'unavailable';
                        const isInactive = isLetAgreed || isUnavailable;
                        return (
                          <div key={`openrent-${listing.id || listing.external_url}`} className="space-y-2">
                            <div className="relative">
                              <PropertyCard
                                property={listing}
                                showActions={false}
                                useOverlayActions={true}
                                isSaved={saved}
                                onSave={() => handleToggleSaveOpenRentListing(listing)}
                                onShare={() => handleShareOpenRentListing(listing)}
                              />
                              {isLetAgreed ? (
                                <div className="absolute left-2 top-2 rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white shadow">
                                  Let Agreed
                                </div>
                              ) : null}
                              {isUnavailable ? (
                                <div className="absolute inset-0 flex items-center justify-center rounded-md bg-slate-700/60">
                                  <span className="rounded bg-slate-900/80 px-3 py-1 text-xs font-semibold text-white">
                                    No Longer Available
                                  </span>
                                </div>
                              ) : null}
                            </div>
                            <Button asChild className="w-full" disabled={isInactive}>
                              <a href={isInactive ? undefined : listing.external_url} target="_blank" rel="noreferrer noopener">
                                View on OpenRent
                                <ExternalLink className="h-4 w-4 ml-2" />
                              </a>
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // Merge and sort by cheap proximity score (postcode/address similarity)
                const mergedAll = [
                  ...availableProperties.map((p) => ({ source: 'internal', item: p })),
                  ...openRentListings.map((l) => ({ source: 'openrent', item: l })),
                ];
                const scored = mergedAll.map((entry) => {
                  const target = entry.item.postcode || entry.item.address || '';
                  return { entry, score: proximityScore(searchKey, target) };
                });
                scored.sort((a, b) => a.score - b.score);
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {scored.map(({ entry }) => {
                      if (entry.source === 'internal') {
                        const property = entry.item;
                        return (
                          <PropertyCard
                            key={`internal-${property.id}`}
                            property={property}
                            onSave={isTenant() ? handleSaveProperty : null}
                            isSaved={savedPropertyIds.has(property.id)}
                          />
                        );
                      }
                      const listing = entry.item;
                      const saved = isOpenRentSaved(listing);
                      const listingStatus = listing.status || 'available';
                      const isLetAgreed = listingStatus === 'let_agreed';
                      const isUnavailable = listingStatus === 'unavailable';
                      const isInactive = isLetAgreed || isUnavailable;
                      return (
                        <div key={`openrent-${listing.id || listing.external_url}`} className="space-y-2">
                          <div className="relative">
                            <PropertyCard
                              property={listing}
                              showActions={false}
                              useOverlayActions={true}
                              isSaved={saved}
                              onSave={() => handleToggleSaveOpenRentListing(listing)}
                              onShare={() => handleShareOpenRentListing(listing)}
                            />
                            {isLetAgreed ? (
                              <div className="absolute left-2 top-2 rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white shadow">
                                Let Agreed
                              </div>
                            ) : null}
                            {isUnavailable ? (
                              <div className="absolute inset-0 flex items-center justify-center rounded-md bg-slate-700/60">
                                <span className="rounded bg-slate-900/80 px-3 py-1 text-xs font-semibold text-white">
                                  No Longer Available
                                </span>
                              </div>
                            ) : null}
                          </div>
                          <Button asChild className="w-full" disabled={isInactive}>
                            <a href={isInactive ? undefined : listing.external_url} target="_blank" rel="noreferrer noopener">
                              View on OpenRent
                              <ExternalLink className="h-4 w-4 ml-2" />
                            </a>
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <Card className="p-12 text-center">
                <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-lg mb-2">No Properties Found</h3>
                <p className="text-muted-foreground mb-4">
                  Try adjusting your search or filters to find more properties
                </p>
                <Button variant="outline" onClick={() => applyExploreFilters({ location: '', minPrice: '', maxPrice: '', bedrooms: '', propertyType: '' }, { forceRefreshExternal: true })}>
                  Clear Filters
                </Button>
              </Card>
            )}

            { (openRentMeta.total_count > openRentListings.length || openRentMeta.has_more) && hasOpenRentFilterInput(exploreFilters) ? (
              <div className="flex justify-center mt-6">
                <Button variant="outline" onClick={loadMoreOpenRentListings} disabled={openRentLoadingMore}>
                  {openRentLoadingMore ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Load more external results
                </Button>
              </div>
            ) : null}
          </section>
        </div>
      </div>

      <AlertDialog open={withdrawConfirmOpen} onOpenChange={setWithdrawConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw Application?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to withdraw this application? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex justify-end gap-3">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleWithdrawApplication} className="bg-destructive hover:bg-destructive/90">
              Withdraw
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};
